import { HandshakeTimeoutError, TimeoutError } from "./errors"
import { isVesselResponse } from "./type-utils"
import type {
  PostMessageOptions,
  ReplyOptions,
  RetryOptions,
  SendOptions,
  VesselMessage,
  VesselMessageListener,
  VesselResponse,
} from "./types"

type VesselOptions = {
  /**
   * The iframe element to connect to as a child vessel.
   * If not provided, the vessel will act as a parent vessel.
   */
  iframe?: HTMLIFrameElement
  /**
   * The target origin for the iframe or parent window to connect to.
   * Defaults to "*" which allows connections from any origin.
   */
  targetOrigin?: string
  /**
  /**
   * An array of allowed orgins to connect to. If targetOrigin is provided it will be added automatically.
   */
  allowedOrigins?: string[]
  /**
   * The application name to use for vessel messages.
   */
  application?: string
  /**
   * Whether to log debug messages to the console.
   */
  debug?: boolean
  /**
   * The timeout in milliseconds for the handshake to complete before throwing.
   */
  handshakeTimeout?: number
  /**
   * The default timeout in milliseconds for sending messages before throwing.
   */
  defaultTimeout?: number
}

/**
 * Event types that can be subscribed to.
 */
type EventType = "message" | "connect"

export class Vessel {
  private readonly type: "parent" | "child"
  private readonly frame: HTMLIFrameElement | undefined
  private targetOrigin: string
  private readonly allowedOrigins: string[] | undefined
  private readonly debug: boolean
  private handshakePromise: Promise<VesselResponse> | undefined
  private readonly handshakeTimeout: number
  private readonly defaultTimeout: number
  private readonly origin: string
  private readonly listeners = new Map<
    EventType,
    VesselMessageListener<unknown>[]
  >()
  private sentMessageCount = BigInt(0)
  private readonly windowMessageEventListener: (event: MessageEvent) => void

  public application: string
  public connected = false

  constructor({
    iframe,
    targetOrigin = "*",
    application = "opensea-vessel",
    debug = false,
    handshakeTimeout = 10_000,
    defaultTimeout = 5000,
    allowedOrigins,
  }: VesselOptions = {}) {
    this.type = iframe ? "parent" : "child"
    this.frame = iframe
    this.targetOrigin = targetOrigin
    this.allowedOrigins =
      targetOrigin !== "*" && allowedOrigins
        ? [...allowedOrigins, targetOrigin]
        : allowedOrigins
    this.application = application
    this.debug = debug
    this.handshakeTimeout = handshakeTimeout
    this.defaultTimeout = defaultTimeout
    this.origin = window.location.origin
    this.addListener("message", this.handshakeMessageListener.bind(this))
    this.windowMessageEventListener = this.handleMessageEvent.bind(this)
    window.addEventListener("message", this.windowMessageEventListener)
  }

  /**
   * Remove all event listeners.
   */
  public cleanup(): void {
    window.removeEventListener("message", this.windowMessageEventListener)
  }

  /**
   * Add a listener to handle incoming messages or lifecycle events (connection).
   * @param listener - Listener to handle messages with
   * @returns Function to remove the listener
   */
  public addListener<ResponseType = unknown>(
    eventName: EventType,
    listener: VesselMessageListener<ResponseType>,
  ): () => void {
    const prevListeners = this.listeners.get(eventName)
    const listeners = prevListeners ? [...prevListeners, listener] : [listener]
    this.listeners.set(
      eventName,
      // Casting to allow adding generic listeners
      listeners as VesselMessageListener<unknown>[],
    )
    return this.removeListener.bind(this, eventName, listener)
  }

  /**
   * Remove a listener from handling incoming messages or lifecycle events (connection).
   * @param listener - Listener to be removed
   */
  public removeListener<ResponseType = unknown>(
    eventName: EventType,
    listener: VesselMessageListener<ResponseType>,
  ): void {
    const listeners = this.listeners.get(eventName)
    this.listeners.set(eventName, listeners?.filter(l => l !== listener) ?? [])
  }

  /**
   * Send a handshake message to connect to iframe or parent window.
   * Returns promise from ongoing or past handshake operation if it exists.
   * @returns Promise that resolves with a handshake response from the connected iframe or parent window
   */
  public async handshake() {
    if (!this.handshakePromise) {
      this.handshakePromise = this.sendHandshakeMessage()
    }
    return this.handshakePromise
  }

  /**
   * Send a payload to connected iframe or parent window and await a response.
   * @param payload - Payload to send to connected iframe or parent window
   * @param options - Options for sending the payload
   * @returns Promise that resolves with a response from the connected iframe or parent window
   */
  public async send<ResponseType = undefined, PayloadType = unknown>(
    payload: PayloadType,
    options: SendOptions = {},
  ): Promise<ResponseType> {
    if (!this.connected) {
      // Shake hands to ensure connection is established
      await this.handshake()
    }
    const response = await this.sendMessage<ResponseType>(
      {
        id: this.createId(),
        payload,
        application: this.application,
      },
      {
        ...options,
        timeout: "timeout" in options ? options.timeout : this.defaultTimeout,
      },
    )
    return response.payload
  }

  private debugLog(...args: unknown[]): void {
    if (this.debug) {
      // eslint-disable-next-line no-console
      console.debug(`[${this.type}]`, ...args)
    }
  }

  private debugTime(label: string): void {
    if (this.debug) {
      // eslint-disable-next-line no-console
      console.time(`[${this.type}] ${label}`)
    }
  }

  private debugTimeEnd(label: string): void {
    if (this.debug) {
      // eslint-disable-next-line no-console
      console.timeEnd(`[${this.type}] ${label}`)
    }
  }

  private warn(...args: unknown[]): void {
    // eslint-disable-next-line no-console
    console.warn(`[${this.type}]`, ...args)
  }

  /**
   * Generate a unique ID for a vessel message.
   * Format is the first letter of the message type followed by message index.
   * Example: "c0" for the first child message sent, "p2" for the third parent message sent.
   *
   * Messages used for internal routines (handshake, response) have an additional "h" or "r" suffix.
   *
   * @returns Unique ID for a vessel message
   */
  private createId(): string {
    return `${this.type[0]}${this.sentMessageCount++}`
  }
  private readonly createResponseId = () => `${this.createId()}r`
  private readonly isResponseId = (id: string) => id.endsWith("r")
  private readonly createHandshakeId = () => `${this.createId()}h`
  private readonly isHandshakeId = (id: string) => id.endsWith("h")

  /**
   * Send a constructed vessel message to connected iframe or parent window.
   * Always use this and never the postMessage API directly.
   * @param message - Vessel message or response message to post using postMessage API
   */
  private postMessage<T>(
    message: VesselMessage<T> | VesselResponse<T>,
    { target }: PostMessageOptions = {},
  ): void {
    this.debugLog(
      `Posting message from ${this.origin} to ${this.targetOrigin}`,
      message,
    )
    if (target) {
      target.postMessage(message, {
        targetOrigin: this.targetOrigin,
      })
    } else if (this.type === "parent") {
      this.frame?.contentWindow?.postMessage(message, {
        targetOrigin: this.targetOrigin,
      })
    } else {
      window.parent.postMessage(message, {
        targetOrigin: this.targetOrigin,
      })
    }
  }

  /**
   * Send a constructed message and await a response.
   * Public API always sends messages using this method so that responses can be awaited.
   * @param message - Vessel message to send using postMessage API
   * @param options - Options for sending the message
   * @returns Promise that resolves with the response from the connected iframe or parent window
   */
  private async sendMessage<ResponseType = unknown>(
    message: VesselMessage,
    { timeout }: SendOptions = {},
  ): Promise<VesselResponse<ResponseType>> {
    return new Promise<VesselResponse<ResponseType>>((resolve, reject) => {
      // Reject if no response is received within timeout milliseconds
      let timeoutId: NodeJS.Timeout
      if (timeout !== undefined) {
        timeoutId = setTimeout(() => {
          reject(new TimeoutError(message, timeout))
          this.removeListener("message", listener)
        }, timeout)
      }

      // Add a listener that resolves the promise when a response is received
      const id = message.id
      const listener = (response: VesselMessage<unknown>) => {
        if (!isVesselResponse(response) || response.originalId !== id) {
          return false // Ignore messages not related to this send operation
        }
        this.debugTimeEnd(id)
        clearTimeout(timeoutId)
        if (response.error) {
          reject(response.payload)
        } else {
          // NOTE: Trusting expected type provided as generic by caller
          resolve(response as VesselResponse<ResponseType>)
        }
        this.removeListener("message", listener)
        return true
      }
      this.addListener("message", listener)

      this.debugTime(message.id)
      this.postMessage(message)
    })
  }

  /**
   * Send a constructed message and retry sending if no response is received.
   * @param message - Vessel message to send using postMessage API
   * @param retryOptions - Options for retrying the send
   * @returns Promise that resolves with the response from the connected iframe or parent window
   */
  private sendMessageWithRetries<T>(
    message: VesselMessage<T>,
    { interval = 100, timeout }: RetryOptions = {},
  ) {
    return new Promise<VesselResponse>((resolve, reject) => {
      let attempts = 1
      let timeoutId: NodeJS.Timeout
      this.sendMessage(message, { timeout })
        .then(resolve)
        .catch(error => {
          if (error instanceof TimeoutError) {
            // Add attempt count to error and reject
            reject(
              new TimeoutError(error.postedMessage, error.timeout, attempts),
            )
          } else {
            reject(error)
          }
        })
        .finally(() => {
          clearTimeout(timeoutId)
        })
      const retry = () => {
        this.debugLog(`Retrying message ${message.id}`)
        this.postMessage(message)
        attempts++
        timeoutId = setTimeout(retry, interval)
      }
      timeoutId = setTimeout(retry, interval)
    })
  }

  /**
   * Reply to a vessel message with a response payload.
   * Must always be used when sending replies to messages.
   * @param message - Vessel message to reply to
   * @param payload - Response payload to reply with
   */
  private reply(
    message: MessageEvent<VesselMessage>,
    payload: unknown = undefined,
    { error }: ReplyOptions = {},
  ): void {
    this.postMessage(
      {
        id: this.createResponseId(),
        payload,
        application: this.application,
        originalId: message.data.id,
        error,
      },
      { target: message.source ?? undefined },
    )
  }

  /**
   * Filters relevant messages and calls listeners with the message event.
   * Keeps track of whether the message was handled by any listener.
   * @param event - Message event to handle
   */
  private async handleMessageEvent(
    event: MessageEvent<VesselMessage>,
  ): Promise<void> {
    if (event.data.application !== this.application) {
      this.debugLog(
        `Ignoring message from ${event.origin} with application ${event.data.application}`,
      )
      return // Ignore messages from other applications
    }
    if (this.allowedOrigins?.includes(event.origin)) {
      this.targetOrigin = event.origin
    }
    if (this.targetOrigin !== "*" && event.origin !== this.targetOrigin) {
      this.debugLog(
        `Ignoring message from ${event.origin} due to target origin of ${this.targetOrigin}`,
      )
      return // Ignore messages from other origins
    }

    const isResponseMessage = this.isResponseId(event.data.id)
    this.debugLog(
      `Received ${isResponseMessage ? "response" : "message"} from ${event.origin}`,
      event.data,
    )

    const listeners = this.listeners.get("message") ?? []
    const result = await Promise.all(
      listeners.map(listener =>
        listener(event.data, this.reply.bind(this, event)),
      ),
    )
    const handledCount = result.reduce(
      (acc, handled) => (handled ? acc + 1 : acc),
      0,
    )
    if (handledCount === 0 && !isResponseMessage) {
      this.warn("Message was not handled", event.data)
    }
    if (handledCount > 1) {
      this.warn(
        `Message was handled by multiple (${handledCount}) listeners`,
        event.data,
      )
    }
  }

  /**
   * Helper function to set connected state and call connect event listeners.
   * @param handshakeResponse - Handshake response message
   */
  private connect(handshakeResponse: VesselMessage): void {
    this.connected = true
    const listeners = this.listeners.get("connect") ?? []
    for (const listener of listeners) {
      listener(handshakeResponse, () => {
        this.warn("Using reply function on connect event is not supported")
      })
    }
  }

  /**
   * Send a handshake message to connect to iframe or parent window.
   * @returns Promise that resolves with a handshake response from the connected iframe or parent window
   */
  private async sendHandshakeMessage() {
    try {
      const handshakeResponse = await this.sendMessageWithRetries(
        {
          id: this.createHandshakeId(),
          payload: "hand",
          application: this.application,
        },
        {
          timeout: this.handshakeTimeout,
        },
      )
      this.connect(handshakeResponse)
      return handshakeResponse
    } catch (error) {
      this.handshakePromise = undefined
      if (error instanceof TimeoutError) {
        throw new HandshakeTimeoutError(
          error.postedMessage,
          error.timeout,
          error.attempts,
        )
      }
      throw error
    }
  }

  /**
   * Handler for handshake messages to connect to iframe or parent window.
   * @param message - Handshake message
   * @param reply - Function to reply to the handshake message
   * @returns Whether the message was handled
   */
  private handshakeMessageListener(
    message: VesselMessage,
    reply: (response: unknown, options?: ReplyOptions) => void,
  ): boolean {
    if (this.isHandshakeId(message.id)) {
      this.connect(message)
      reply("shake!")
      return true
    }
    return false
  }
}
